import { computed, ComputedRef, type Ref, unref } from 'vue';
import { useLiveQueryV2, CODES } from '@libs/use';
import { waitFor } from '@libs/utils';
import type { MarketDepthItems, MarketDepthsQuery } from '@libs/api-reference';
import { createBufferedThrottle } from '@libs/utils';
import { AxiosPromise } from '@libs/http';

import {
  MarketDepthSignal,
  transformMarketDepthChange,
} from '../utils/transforms';
import { indexFactory, parentFactory } from '../utils/marketDepth';
import { SYMBOL_MARKET_DEPTH_CHANNEL_PREFIX } from '../constants';

type UseLiveMarketDepthQueryFactoryType = {
  result: Ref<MarketDepthItems[]>;
  execute: ReturnType<typeof useLiveQueryV2>['execute'];
  isLoadingMarketDepth: Ref<boolean>;
  isRevalidatingMarketDepth: Ref<boolean>;
  isErrorMarketDepth: Ref<boolean>;
  marketDepthStatus: ReturnType<typeof useLiveQueryV2>['status'];
};

type useLiveMarketDepthQueryFactoryConfigs = {
  key: any[];
  isin: ComputedRef<string | null>;
  type: number;
  fetcher: (...arg: any[]) => AxiosPromise;
  sorter: (a: number, b: number) => number;
  immediate: boolean;
};

export default function useLiveMarketDepthQueryFactory({
  key,
  isin,
  type,
  sorter,
  fetcher,
  immediate,
}: useLiveMarketDepthQueryFactoryConfigs): UseLiveMarketDepthQueryFactoryType {
  const {
    result,
    execute,
    isPending: isLoadingMarketDepth,
    isRevalidating: isRevalidatingMarketDepth,
    isError: isErrorMarketDepth,
    status: marketDepthStatus,
  } = useLiveQueryV2<MarketDepthsQuery>({
    getStaticChannels: () => [
      `${SYMBOL_MARKET_DEPTH_CHANNEL_PREFIX}${unref(isin)}`,
    ],
    immediate,
    fetcher,
    key,
    onUpdate: createBufferedThrottle<MarketDepthSignal>(250, (signals) => {
      const actions = {
        [CODES.SymbolMarketDepthChangeSignal]: transformMarketDepthChange,
      };

      const isLoadingOrRevalidating =
        isLoadingMarketDepth.value ||
        isRevalidatingMarketDepth.value ||
        !result.value?.data.items;
      if (isLoadingOrRevalidating) return { consumed: false };

      const items = result.value.data.items ?? [];
      for (const signal of signals) {
        const transform = actions[signal.code];
        if (!transform) continue;

        const data = transform(signal.data);

        // prevent invalid processing while switching symbol in stockwatch
        if (data.isin !== isin.value) continue;

        if (data.type !== type) continue;

        let parentIndex: number;
        if (data.price) {
          parentIndex = findParentIndex(items, data.price, sorter);
        } else {
          // edge cases when price isn't available (e.g. in remove event)
          parentIndex = items.findIndex((item) =>
            item.depthModels?.some(
              (child) => child.uniqueKey === data.uniqueKey
            )
          );
        }

        const parent = parentIndex >= 0 ? items[parentIndex] : undefined;

        const childIndex =
          parent?.depthModels?.findIndex(
            (item) => item.uniqueKey === data.uniqueKey
          ) ?? -1;

        if (data.changeType === 'create') {
          if (!parent) {
            const newParent = parentFactory(data, indexFactory(data));

            const index = findInsertIndex(items, newParent.price, sorter);
            items.splice(index, 0, newParent);
          } else {
            parent.depthModels?.push(indexFactory(data));
            parent.volume = recalculateVolume(parent);
          }
        } else if (data.changeType === 'modify') {
          if (!parent) {
            const newParent = parentFactory(data, indexFactory(data));

            const index = findInsertIndex(items, newParent.price, sorter);
            items.splice(index, 0, newParent);
          } else if (childIndex > -1) {
            parent.depthModels?.splice(childIndex, 1, indexFactory(data));
            parent.volume = recalculateVolume(parent);
          }
        } else if (data.changeType === 'remove') {
          if (parent && childIndex > -1) {
            parent.depthModels?.splice(childIndex, 1);
            parent.volume = recalculateVolume(parent);

            if (!parent.depthModels?.length) {
              items.splice(parentIndex, 1);
            }
          }
        } else if (data.changeType === 'remove-all') {
          result.value.data.items = [];

          waitFor(5000).then(execute);
        }
      }

      return { consumed: true };
    }),
  });

  const marketDepth = computed(() => {
    return result.value?.data.items || [];
  });

  return {
    execute,
    result: marketDepth,
    isLoadingMarketDepth,
    isRevalidatingMarketDepth,
    isErrorMarketDepth,
    marketDepthStatus,
  };
}

function findInsertIndex(
  arr: MarketDepthItems[],
  price: number,
  compare: (a: number, b: number) => number
) {
  let left = 0;
  let right = arr.length;

  while (left < right) {
    const mid = (left + right) >> 1;

    if (compare(arr[mid].price, price) < 0) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  return left;
}

function findParentIndex(
  arr: MarketDepthItems[],
  price: number,
  compare: (a: number, b: number) => number
): number {
  let left = 0;
  let right = arr.length;

  while (left < right) {
    const mid = (left + right) >> 1;
    const cmp = compare(arr[mid].price, price);

    if (cmp < 0) {
      left = mid + 1;
    } else if (cmp > 0) {
      right = mid;
    } else {
      return mid; // found exact match
    }
  }

  return -1;
}

function recalculateVolume(depth: MarketDepthItems) {
  return (
    depth.depthModels?.reduce(
      (sum, item) => sum + item.orderDisplayedQuantity,
      0
    ) ?? 0
  );
}
